import * as THREE from "three";

/**
 * Литейня в 3D (консультант 04.11.2026: «арена почти вся чёрная пустота… нужны стены, свет из горна, глубина»):
 * кирпичная стена в глубине, колонны, канал расплавленного металла вдоль стены с горячим светом, плиты пола, туман.
 * Всё кодом, без сторонних файлов; мерцание огня — от кадра мира (одинаково при откате). Плывущий вверх дым убран
 * (Джон 04.11.2026, 23:30: «непонятных пятен… плывут всегда снизу вверх… выглядят дёшево»): атмосфера — светом и дымкой.
 * Единица — пиксель мира, линия боя — z = 0, пол — y = 0.
 */
const WALL_Z = -340;
const CHANNEL_Z = -230;
const WIDTH = 2600;
const PILLARS = 9;

function brickTexture(): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 256;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas 2D недоступен");
  context.fillStyle = "#1a1214";
  context.fillRect(0, 0, 512, 256);
  for (let row = 0; row < 16; row++) {
    for (let column = 0; column < 9; column++) {
      const x = column * 64 - (row % 2 ? 32 : 0);
      const shade = 34 + ((row * 7 + column * 13) % 5) * 6;
      context.fillStyle = `rgb(${shade + 14}, ${shade - 4}, ${shade - 8})`;
      context.fillRect(x + 2, row * 16 + 2, 60, 12);
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(8, 2);
  return texture;
}

function plateTexture(): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 256;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas 2D недоступен");
  context.fillStyle = "#231c1e";
  context.fillRect(0, 0, 256, 256);
  context.strokeStyle = "#120c0e";
  context.lineWidth = 4;
  context.strokeRect(2, 2, 252, 252);
  context.fillStyle = "#3a3034";
  for (const [x, y] of [
    [16, 16],
    [240, 16],
    [16, 240],
    [240, 240],
  ] as const)
    context.fillRect(x - 4, y - 4, 8, 8);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(26, 8);
  return texture;
}

export class Foundry3D {
  private readonly forgeLights: THREE.PointLight[] = [];

  constructor(scene: THREE.Scene, centerX: number) {
    scene.fog = new THREE.FogExp2(0x160c08, 0.00085);
    scene.background = new THREE.Color(0x0c0706);
    // пол — плиты
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(WIDTH, 1400),
      new THREE.MeshStandardMaterial({ map: plateTexture(), color: 0x242022, roughness: 0.9, metalness: 0.25 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(centerX, 0, -200);
    floor.receiveShadow = true;
    scene.add(floor);
    // стена в глубине
    const wall = new THREE.Mesh(
      new THREE.PlaneGeometry(WIDTH, 900),
      new THREE.MeshStandardMaterial({ map: brickTexture(), color: 0x3a3030, roughness: 0.95, metalness: 0 }),
    );
    wall.position.set(centerX, 450, WALL_Z);
    wall.receiveShadow = true;
    scene.add(wall);
    // колонны с балкой — глубина и ритм
    const pillarMaterial = new THREE.MeshStandardMaterial({ color: 0x2a2226, roughness: 0.6, metalness: 0.5 });
    for (let index = 0; index < PILLARS; index++) {
      const x = centerX - WIDTH / 2 + 150 + (index * (WIDTH - 300)) / (PILLARS - 1);
      const pillar = new THREE.Mesh(new THREE.BoxGeometry(46, 720, 46), pillarMaterial);
      pillar.position.set(x, 360, WALL_Z + 60);
      pillar.castShadow = true;
      pillar.receiveShadow = true;
      scene.add(pillar);
    }
    const beam = new THREE.Mesh(new THREE.BoxGeometry(WIDTH, 40, 60), pillarMaterial);
    beam.position.set(centerX, 560, WALL_Z + 60);
    scene.add(beam);
    // канал расплавленного металла вдоль стены: светится сам и светит на пол и бойцов сзади
    const channel = new THREE.Mesh(
      new THREE.BoxGeometry(WIDTH, 10, 50),
      new THREE.MeshStandardMaterial({ color: 0x6a2008, emissive: 0xff5a10, emissiveIntensity: 0.9 }),
    );
    channel.position.set(centerX, -3, CHANNEL_Z); // щель в полу: светится, но не полосой у ног
    scene.add(channel);
    for (let index = 0; index < 4; index++) {
      const light = new THREE.PointLight(0xff7a2a, 5200, 700, 1.7);
      light.position.set(centerX - 900 + index * 600, 60, CHANNEL_Z + 30);
      scene.add(light);
      this.forgeLights.push(light);
    }
  }

  /** Огонь на кадр мира. */
  update(tick: number): void {
    this.forgeLights.forEach((light, index) => {
      light.intensity = 5200 * (0.82 + 0.18 * Math.sin(tick * 0.21 + index * 1.7) * Math.sin(tick * 0.07 + index));
    });
  }
}
